import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSpinner,
  faCircleCheck,
  faCircleXmark,
  faTriangleExclamation,
  faLink,
  faHourglassEnd,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { formatInTimeZone } from "date-fns-tz";
import { AppDispatch, RootState } from "../../store/indexRedux";
import { fetchPortfolioServices } from "../../store/companyPortfolioSlice";
import {
  sendEndOfContractRequest,
  fetchEndOfContractRequests,
  EndOfContractError,
  EndOfContractRequestBody,
  EndOfContractRequestRow,
  normalizeEan,
} from "../../api/endOfContract";
import {
  FlexService,
  InjectionService,
  OfftakeService,
} from "../../types/portfolioTypes";
import { FlexAssetType, getFlexContractStatus } from "../../functions/flexServices";
import { MonthPicker } from "../ui/MonthPicker";
import { DatePicker } from "../ui/DatePicker";
import { isEocRetryBlocked } from "../../utils/eocRetryGuard";
/**
 * The confirmed-EOC outcome gate. `/allservices` `eocStatus` mirrors the raw
 * Atrias StatusType code ('39' approved / '41' rejected), while the label
 * vocabulary ('approved'/'rejected') lives on `mig6_eoc_requests.status` — the
 * SEPARATE transient-'pending' endpoint read below as `pendingByEan`, never
 * cross-compared with this one. Both vocabularies are accepted so the rollout
 * order across parser/backend/frontend does not matter; see src/utils/eocStatus.
 *
 * TWO readings of that column, deliberately not interchangeable:
 * - `normalizeEocStatus` → the LOCK (endability). Any '39'/'approved' leg is
 *   terminal and non-endable, whatever the reason says.
 * - `eocBadgeOutcome`    → the BADGE (wording only). Suppresses the customer
 *   switch-away ('39' + reason 'COMPLETED'), which is a handover to another
 *   supplier and not an end-of-contract the customer requested.
 */
import { EocOutcome, eocBadgeOutcome, normalizeEocStatus } from "../../utils/eocStatus";

type Direction = "injection" | "offtake";

/** User's choice on a combined EAN that allows directional EOC. */
type EocDirection = "offtake" | "injection" | "both";

/** Per-leg confirmed EOC outcome off one `/allservices` sdps row. */
interface EocLeg {
  /**
   * ENDABILITY. Read by every selection predicate (approvedDirectionsOf →
   * endableDirectionsOf → isCommoditySelectable → the disabled checkbox, and
   * the submit-batch filter). Reason-blind on purpose: a switched-away SDP is
   * just as terminal as an EOC-ended one and must never become re-submittable.
   */
  status: EocOutcome;
  /**
   * WORDING ONLY. Same value, minus the customer switch-away ('39' + reason
   * 'COMPLETED' — the SDP went to another supplier, no EOC was requested), so
   * that row shows no EOC outcome badge while staying locked via `status`.
   * Never read by a selection predicate.
   */
  badgeStatus: EocOutcome;
  eocDate: string | null;
  reason: string | null;
  /**
   * The contract's own end date on this leg (`/allservices` endDate). Non-null
   * (past OR future) → the leg is ALREADY ENDING (post-loss, or a non-EOC end)
   * — the terminal-most lifecycle state, superseding accepted/pending.
   */
  contractEndDate: string | null;
}

interface CommodityEligibleContract {
  kind: "commodity";
  /** Normalized EAN — doubles as the selection key. */
  key: string;
  ean: string;
  directions: Direction[];
  /**
   * Combined (SC_OFFINJE) EAN on a NON-Fluvius grid operator → the user may
   * end one direction at a time. OR across the source injection/offtake rows.
   */
  allowsDirectionalEoc: boolean;
  /**
   * Set on the EFFECTIVE copy when the user narrowed a combined EAN to one
   * direction — drives the POST `direction` field. Absent = whole EAN.
   */
  eocDirection?: "offtake" | "injection";
  /**
   * Legs whose source row has physicalStatus 'Under construction' — not
   * endable. EANs where NOTHING remains endable are excluded in the builder;
   * a combined+directional EAN with ONE leg under construction survives with
   * the direction choice FORCED to the connected leg.
   */
  underConstruction: Direction[];
  /**
   * Confirmed Atrias EOC outcome per leg (FT-4389), from `/allservices`:
   * offtake row → offtake leg, injection row → injection leg. 'approved' legs
   * are non-endable and render "Accepted — ends <eocDate>"; 'rejected' legs
   * stay re-requestable and render the rejection reason. Terminal state wins
   * over the transient GET 'pending' badge.
   */
  eocByDirection: Partial<Record<Direction, EocLeg>>;
  address: string;
  siteName: string | null;
  siteId: number | null;
  injectionInstallationId: string | null;
  offtakeInstallationId: string | null;
}

interface FlexEligibleContract {
  kind: "flex";
  /**
   * `flex-${contract_flex_id}` — unique per flex CONTRACT. Never key on the
   * flex row's `id`: that is the sites-table id, shared by ALL flex services
   * on the same site (a BESS and a PV on one site would collide).
   */
  key: string;
  contractFlexId: number;
  assetType: FlexAssetType;
  name: string;
  address: string;
  /** The flex row IS a sites-table row, so its id is the site id. */
  siteId: number;
  injectionInstallationId: string | null;
  offtakeInstallationId: string | null;
}

type EligibleContract = CommodityEligibleContract | FlexEligibleContract;

/**
 * Per-contract submit outcome (keyed by EligibleContract.key). EOC POSTs are
 * irreversible once sent, so a partially failed batch must never invite a
 * blind full retry (the already-sent requests would 409):
 * - "sent"    — POST succeeded (or, on a RETRY pass, 409'd as already
 *               existing — see runSubmitPass). Permanent for the component's
 *               lifetime: never re-submittable, never deselectable.
 * - "failed"  — POST failed; retryable. Transient (cleared on back-to-form).
 * - "notSent" — aborted by stop-on-first-error; retryable. Transient.
 */
type SubmitOutcome =
  | { state: "sent"; requestId: string | null; alreadyProcessed: boolean }
  | { state: "failed"; message: string }
  | { state: "notSent" };

/**
 * Frozen at the first submit click. Retries must run against THIS set — the
 * post-success portfolio refresh drops directly-ended flex rows out of the
 * live eligible list, which would otherwise shrink the retry set.
 */
interface SubmitBatch {
  /** What the FE actually POSTs (commodities + independently ticked flex). */
  toSubmit: EligibleContract[];
  /** What the confirm step displays (submit set + informative linked flex). */
  display: EligibleContract[];
  /**
   * Linked flex (batch union), frozen with the batch: every commodity POST
   * carries ITS OWN linked subset as bess/eboiler/pv contract ids, and the
   * post-success portfolio refresh must not change what a retry sends.
   */
  linkedFlex: FlexEligibleContract[];
  /**
   * Effective date for commodity POSTs — also the date the auto-linked flex
   * end on (Atrias cascade). Frozen so retries resend the original date.
   */
  commodityEocDate: string;
  /** Effective date for the independent-flex POSTs (one shared flex date). */
  flexEocDate: string;
}

/**
 * contract_flex_ids of the flex linked to ONE specific commodity (NOT the
 * batch union), mapped by asset type for the commodity POST body. If
 * injection and offtake both link the same BESS, both POSTs deliberately
 * carry the same bess_contract_id — the BE/mig6-ws dedupe via the end_date
 * guard; the FE must not dedupe.
 */
const buildLinkedFlexIds = (
  commodity: CommodityEligibleContract,
  linkedFlex: FlexEligibleContract[]
): Pick<
  Extract<EndOfContractRequestBody, { contractType: "commodity" }>,
  "bess_contract_id" | "eboiler_contract_id" | "pv_contract_id"
> => {
  const ids: {
    bess_contract_id?: number;
    eboiler_contract_id?: number;
    pv_contract_id?: number;
  } = {};
  for (const flex of linkedFlex) {
    if (!isLinkedFlex(flex, commodity)) continue;
    if (flex.assetType === "BESS") ids.bess_contract_id = flex.contractFlexId;
    else if (flex.assetType === "e-boiler") ids.eboiler_contract_id = flex.contractFlexId;
    else ids.pv_contract_id = flex.contractFlexId;
  }
  return ids;
};

/**
 * FT-4389 business rule: the earliest EOC date for ANY combination of contracts
 * (commodity AND flex) — the first day of the month AFTER next, as seen in
 * Europe/Brussels (DST-aware via date-fns-tz; pure calendar math thereafter, so
 * no DST edge cases). Example: today 10/07 → 01/09.
 *
 * This value is BOTH the DEFAULT and the MINIMUM (month granularity) of the
 * user-selectable end date: the user may pick this month or any LATER month.
 * The backend re-validates the chosen effectiveDate against the same formula
 * (BE authoritative) and rejects anything earlier with EFFECTIVE_DATE_TOO_SOON.
 *
 * This is always ≥ 29 calendar days out (worst case: today = 31 Jan → 01 Mar),
 * so it clears the backend's 28-day commodity notice minimum by construction.
 */
const computeEocDate = (): string => {
  const brusselsToday = formatInTimeZone(new Date(), "Europe/Brussels", "yyyy-MM-dd");
  const [y, m] = brusselsToday.split("-").map(Number);
  // m is 1-based; first day of the month-after-next = month index (m-1) + 2.
  // The Date constructor normalizes overflow across year boundaries.
  const target = new Date(y, m - 1 + 2, 1);
  const mm = String(target.getMonth() + 1).padStart(2, "0");
  return `${target.getFullYear()}-${mm}-01`;
};

/**
 * Ticket FT-4389 linkage rules ("same rules as onboarding"):
 * - injection ends BESS + PV curtailment
 * - offtake ends BESS + e-boiler
 * - injection AND offtake selected together → ALL flex
 * Linked services end TOGETHER. Matching: the flex row's injection/offtake
 * installation refs against the commodity services' installation_id (the
 * PortfolioServices pattern), falling back to the site linkage (flex row id =
 * commodity site_id — both are sites-table ids) when the refs don't resolve.
 */
/** Legs with a confirmed 'approved' outcome — already ending, non-endable. */
const approvedDirectionsOf = (c: CommodityEligibleContract): Direction[] =>
  c.directions.filter((d) => c.eocByDirection[d]?.status === "approved");

/** Legs with a confirmed 'rejected' outcome — re-requestable (still endable). */
const rejectedDirectionsOf = (c: CommodityEligibleContract): Direction[] =>
  c.directions.filter((d) => c.eocByDirection[d]?.status === "rejected");

/**
 * Legs whose BADGE outcome is `outcome` — the DISPLAY-ONLY view of the same
 * column, with the customer switch-away suppressed (see EocLeg.badgeStatus).
 * Badge rendering reads this; endability never does. For 'rejected' it is
 * currently identical to `rejectedDirectionsOf` (only 'approved' is
 * suppressed) — routed through the badge field anyway so every badge on this
 * surface has ONE source and cannot drift from the rule.
 */
const badgeDirectionsOf = (
  c: CommodityEligibleContract,
  outcome: "approved" | "rejected"
): Direction[] => c.directions.filter((d) => c.eocByDirection[d]?.badgeStatus === outcome);

/** A contract end date that actually marks an ending (non-null, non-zero). */
const isEndingDate = (d: string | null | undefined): boolean =>
  !!d && d !== "0000-00-00";

/**
 * Legs whose contract `endDate` is already set (past OR future) — ALREADY
 * ENDING. The terminal-most lifecycle state: supersedes approved/pending and
 * makes the leg non-endable (backed BE-side by the 409 CONTRACT_ALREADY_ENDING
 * backstop).
 */
const alreadyEndingDirectionsOf = (c: CommodityEligibleContract): Direction[] =>
  c.directions.filter((d) => isEndingDate(c.eocByDirection[d]?.contractEndDate));

/**
 * Legs that can still be (re)ended now: excludes under-construction legs, legs
 * already 'approved' (ending), and legs already ending via a set contract
 * endDate. 'rejected' legs remain endable — a rejection is re-requestable.
 */
const endableDirectionsOf = (c: CommodityEligibleContract): Direction[] => {
  const blocked = new Set<Direction>([
    ...c.underConstruction,
    ...approvedDirectionsOf(c),
    ...alreadyEndingDirectionsOf(c),
  ]);
  return c.directions.filter((d) => !blocked.has(d));
};

/**
 * Whether the row can still be submitted. Mirrors the whole-EAN vs directional
 * rule: a partially-blocked combined EAN (one leg UC/approved) is only
 * submittable when directional EOC is allowed — otherwise the sole shape is a
 * whole-EAN end, which the mixed state blocks. Fully-blocked → not selectable
 * (terminal), rendered locked with its outcome badge.
 */
const isCommoditySelectable = (c: CommodityEligibleContract): boolean => {
  const endable = endableDirectionsOf(c);
  return (
    endable.length > 0 &&
    (endable.length === c.directions.length || c.allowsDirectionalEoc)
  );
};

/**
 * Segmented-control options for a commodity row. Empty → no selector
 * (single-direction EAN, combined without directional eligibility, or a
 * fully-terminal row). One option → the choice is FORCED (combined+directional
 * EAN with the other leg under construction OR already approved: only the
 * remaining leg is endable — no 'Both').
 */
const directionOptionsFor = (c: CommodityEligibleContract): EocDirection[] => {
  if (c.directions.length !== 2 || !c.allowsDirectionalEoc) return [];
  const endable = endableDirectionsOf(c);
  if (endable.length === 0) return [];
  return endable.length === 1 ? [endable[0]] : ["offtake", "injection", "both"];
};

const isLinkedFlex = (
  flex: FlexEligibleContract,
  commodity: CommodityEligibleContract
): boolean => {
  const injectionLinkable =
    commodity.directions.includes("injection") &&
    (flex.assetType === "BESS" || flex.assetType === "PV curtailment");
  const offtakeLinkable =
    commodity.directions.includes("offtake") &&
    (flex.assetType === "BESS" || flex.assetType === "e-boiler");
  if (!injectionLinkable && !offtakeLinkable) return false;

  if (
    injectionLinkable &&
    flex.injectionInstallationId &&
    flex.injectionInstallationId === commodity.injectionInstallationId
  ) {
    return true;
  }
  if (
    offtakeLinkable &&
    flex.offtakeInstallationId &&
    flex.offtakeInstallationId === commodity.offtakeInstallationId
  ) {
    return true;
  }

  // Site fallback: same sites-table id → same site.
  return commodity.siteId != null && flex.siteId === commodity.siteId;
};

const EndContractManager: React.FC = () => {
  const { t } = useTranslation("management");
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const accessToken = useSelector((state: RootState) => state.authv2.accessToken);
  const selectedCompanyId = useSelector(
    (state: RootState) => state.selectedCompany.selectedUserCompany?.company_id
  );
  const selectedCompanyNumber = useSelector(
    (state: RootState) => state.selectedCompany.selectedUserCompany?.companyNumber
  );
  const portfolio = useSelector((state: RootState) => state.companyPortfolio.data);
  const portfolioLoading = useSelector(
    (state: RootState) => state.companyPortfolio.loading
  );
  /**
   * A failed `/allservices` must be distinguishable from an empty portfolio —
   * rendering "no active contracts" for a fetch failure tells the customer
   * something untrue about their own account.
   */
  const portfolioError = useSelector(
    (state: RootState) => state.companyPortfolio.error
  );
  /**
   * FE AFFORDANCE ONLY — never a security boundary. An admin gets a day-level
   * picker with no minimum; the backend re-resolves admin from the JWT
   * (`isAdminContact`) and skips the minimum-date + first-of-month checks on
   * that basis alone. Nothing about the role is ever sent in the request body.
   * Same expression as ManagementPage.
   */
  const isAdmin = useSelector(
    (state: RootState) => state.user.user?.role === "admin"
  );

  // FT-4389: the default AND minimum EOC date (first day of the
  // month-after-next, Brussels) for ANY combination of contracts. A non-admin
  // may select this month or any later month via the MonthPicker below; an
  // admin may select any day (see isAdmin).
  //
  // Deliberately NOT memoized: `useMemo(..., [])` froze this at mount, so a tab
  // left open across a month boundary kept offering — and the retry guard kept
  // validating against — a minimum that had already expired. computeEocDate()
  // is cheap (one formatInTimeZone + calendar math) and returns a string, so
  // calling it per render is safe: the value is referentially equal by VALUE,
  // which is what the reset effect's dependency array compares. No render loop.
  // When the month does roll over the string changes exactly once, and the
  // reset effect below intentionally re-defaults the form to the new minimum.
  const eocDate = computeEocDate();

  const [step, setStep] = useState<"form" | "confirm" | "success">("form");
  const [selectedCommodityKeys, setSelectedCommodityKeys] = useState<Set<string>>(
    new Set()
  );
  const [selectedFlexKeys, setSelectedFlexKeys] = useState<Set<string>>(new Set());
  // FT-4389: a SINGLE user-selected end date (month granularity) applies to
  // every contract kind. Kept as two state vars (both initialised to the
  // default computed date) so the frozen-batch and confirm/retry machinery that
  // reads per-kind dates keeps working unchanged; the MonthPicker's onChange
  // updates BOTH in lockstep, so commodityEocDate and flexEocDate are always
  // equal.
  const [commodityEocDate, setCommodityEocDate] = useState<string>(eocDate);
  const [flexEocDate, setFlexEocDate] = useState<string>(eocDate);
  const [submitting, setSubmitting] = useState(false);
  const [outcomes, setOutcomes] = useState<Map<string, SubmitOutcome>>(new Map());
  const [frozenBatch, setFrozenBatch] = useState<SubmitBatch | null>(null);
  /** Per-EAN direction choice for combined EANs allowing directional EOC. */
  const [directionChoices, setDirectionChoices] = useState<Map<string, EocDirection>>(
    new Map()
  );
  /** Latest EOC request per EAN from the backend (pending indicator). */
  const [eocRequests, setEocRequests] = useState<EndOfContractRequestRow[]>([]);

  /**
   * Has the user explicitly PICKED an end date (as opposed to accepting the
   * default)?
   *
   * The re-default below must never overwrite a deliberate choice, and `step`
   * was the wrong proxy for that. A step-based guard reads the CURRENT step, and
   * on confirm → form (the Back button) the step legitimately becomes "form"
   * again — so a rollover first observed on that render passed the guard and
   * clobbered the pick. Worse, the substituted value is a valid first-of-month,
   * so nothing rejects it: the contract would just end on a date nobody chose,
   * with a clean success.
   *
   * So the flag tracks the thing that actually matters. INVARIANT: while it is
   * false, commodityEocDate and flexEocDate are exactly computeEocDate() — the
   * initial state, the company-change reset and the re-default itself all
   * preserve that — which is why re-defaulting an untouched date is never
   * destructive. Only `handleEocDateChange` sets it; only the company-change
   * reset clears it.
   */
  const [eocDateTouched, setEocDateTouched] = useState(false);

  /**
   * COMPANY CHANGE → reset the whole flow. UNCONDITIONAL, at every step, on
   * purpose: the frozen batch, outcomes, selections and direction choices all
   * belong to the PREVIOUS company. The company switcher lives in the header,
   * so it is reachable from `confirm` and `success` too, and `handleRetry`
   * would otherwise call runSubmitPass(newCompanyId, staleFrozenBatch) and
   * POST company A's EANs under company B's id. (The backend contains that —
   * GetCompanyInfo is keyed by ean AND company_id, so it 404s NO_SDP and
   * nothing corrupts — but the screen would still be showing one company's
   * contracts while acting as another.)
   *
   * Deliberately NOT dependent on `eocDate`: a month rollover must not wipe a
   * batch mid-flow (that is the effect below, which is narrower). The current
   * minimum is read inline instead, so the re-default is still correct.
   */
  useEffect(() => {
    const currentMinimum = computeEocDate();
    setStep("form");
    setSelectedCommodityKeys(new Set());
    setSelectedFlexKeys(new Set());
    setCommodityEocDate(currentMinimum);
    setFlexEocDate(currentMinimum);
    // A new company starts from a fresh default, never the previous user's pick.
    setEocDateTouched(false);
    setOutcomes(new Map());
    setFrozenBatch(null);
    setDirectionChoices(new Map());
    setEocRequests([]);
  }, [selectedCompanyId]);

  /**
   * MONTH ROLLOVER (Brussels) → move the end date to the new minimum, but ONLY
   * while the user has not picked one themselves.
   *
   * Both halves are load-bearing:
   * - UNTOUCHED dates MUST follow the rollover. The previous default is below
   *   the new minimum, so leaving it would hand a non-admin a value the backend
   *   rejects with EFFECTIVE_DATE_TOO_SOON.
   * - A TOUCHED date is never moved, at ANY step and across any number of
   *   re-renders. It is theirs until they change it.
   *
   * Guarded on `eocDateTouched` rather than on `step`: see that flag's note for
   * why the step was a leaky proxy. Deliberately NOT merged into the
   * company-change effect above — a top-level guard there would suppress the
   * company reset too, which is the cross-company stale-state bug it describes.
   */
  useEffect(() => {
    if (eocDateTouched) return;
    setCommodityEocDate(eocDate);
    setFlexEocDate(eocDate);
  }, [eocDate, eocDateTouched]);

  /**
   * The ONE writer of the end date. FT-4389 keeps a SINGLE user-selected date
   * for every contract kind, so these two state vars must never diverge; both
   * the admin DatePicker and the non-admin MonthPicker share this handler
   * rather than each inlining its own copy of the lockstep write.
   */
  const handleEocDateChange = useCallback((val: string) => {
    setCommodityEocDate(val);
    setFlexEocDate(val);
    // From here on the rollover re-default must leave this value alone.
    setEocDateTouched(true);
  }, []);

  /**
   * Pending-indicator load: on mount / company change, and refetched after
   * every submit pass with ≥1 commodity success. A failed load must never
   * break the page — log and proceed (the backend 409s are the backstop).
   */
  const loadEocRequests = useCallback(async () => {
    if (!accessToken || !selectedCompanyId) return;
    try {
      const rows = await fetchEndOfContractRequests(selectedCompanyId, accessToken);
      setEocRequests(rows);
    } catch (err) {
      console.error("Failed to load end-of-contract request statuses", err);
    }
  }, [accessToken, selectedCompanyId]);

  useEffect(() => {
    void loadEocRequests();
  }, [loadEocRequests]);

  /**
   * EANs with a PENDING request — locked in the form. For combined EANs the
   * pending direction comes from the request's Atrias service component.
   */
  const pendingByEan = useMemo(() => {
    const map = new Map<string, "offtake" | "injection" | "whole">();
    for (const row of eocRequests) {
      if (row.status !== "pending") continue;
      map.set(
        normalizeEan(row.ean),
        row.service_component === "SC_COMMOFF"
          ? "offtake"
          : row.service_component === "SC_COMMINJ"
            ? "injection"
            : "whole"
      );
    }
    return map;
  }, [eocRequests]);

  const eligibleContracts = useMemo<EligibleContract[]>(() => {
    // Commodity contracts (injection/offtake) — deduped per EAN → combined.
    // OFFINJE/DMR points therefore always end offtake + injection together.
    const map = new Map<string, CommodityEligibleContract>();
    const add = (s: InjectionService | OfftakeService, direction: Direction) => {
      // Eligibility is decided by the active status alone. The `latest` flag is unreliable
      // (can sit at 0 on a genuinely current contract) and the backend already returns the
      // current contract per SDP, so gating on it wrongly hid active services (see #461).
      if (s.status !== "active") return;
      const key = normalizeEan(s.ean);
      const underConstruction = s.physicalStatus === "Under construction";
      const leg: EocLeg = {
        status: normalizeEocStatus(s.eocStatus),
        badgeStatus: eocBadgeOutcome(s.eocStatus, s.eocStatusReason),
        eocDate: s.eocDate,
        reason: s.eocStatusReason,
        contractEndDate: s.endDate,
      };
      const existing = map.get(key);
      if (existing) {
        if (!existing.directions.includes(direction)) existing.directions.push(direction);
        if (!existing.eocByDirection[direction]) existing.eocByDirection[direction] = leg;
        if (underConstruction && !existing.underConstruction.includes(direction)) {
          existing.underConstruction.push(direction);
        }
        if (direction === "injection" && !existing.injectionInstallationId) {
          existing.injectionInstallationId = s.installation_id;
        }
        if (direction === "offtake" && !existing.offtakeInstallationId) {
          existing.offtakeInstallationId = s.installation_id;
        }
        if (existing.siteId == null) existing.siteId = s.site_id;
        // EITHER source row carrying the flag enables the direction choice.
        existing.allowsDirectionalEoc =
          existing.allowsDirectionalEoc || !!s.allows_directional_eoc;
      } else {
        map.set(key, {
          kind: "commodity",
          key,
          ean: key,
          directions: [direction],
          allowsDirectionalEoc: !!s.allows_directional_eoc,
          underConstruction: underConstruction ? [direction] : [],
          eocByDirection: { [direction]: leg },
          address: `${s.addressStreet} ${s.addressNumber}, ${s.addressZip} ${s.addressCity}`,
          siteName: s.site_name,
          siteId: s.site_id,
          injectionInstallationId: direction === "injection" ? s.installation_id : null,
          offtakeInstallationId: direction === "offtake" ? s.installation_id : null,
        });
      }
    };
    (portfolio?.services.injection ?? []).forEach((s) => add(s, "injection"));
    (portfolio?.services.offtake ?? []).forEach((s) => add(s, "offtake"));

    // Flex contracts (BESS / PV curtailment / e-boiler) — derived status must
    // be "active" AND no end_date already set (a FUTURE end_date still derives
    // "active", but such a contract is already ending — mirrors the backend's
    // ALREADY_ENDING guard). Identified by contract_flex_id (no EAN).
    const flex: FlexEligibleContract[] = [];
    // assetType comes from the backend's authoritative bucketing (which array
    // the row arrives in) — NOT re-derived via getFlexAssetType, whose
    // default-to-BESS fallback can mislabel rows with missing product info.
    const addFlex = (s: FlexService, assetType: FlexAssetType) => {
      if (!s.contract_flex_id || getFlexContractStatus(s) !== "active") return;
      if (s.end_date && s.end_date !== "0000-00-00") return;
      flex.push({
        kind: "flex",
        key: `flex-${s.contract_flex_id}`,
        contractFlexId: s.contract_flex_id,
        assetType,
        name: s.name,
        address: `${s.address_street} ${s.address_number}, ${s.address_zip} ${s.address_city}`,
        siteId: s.id,
        injectionInstallationId: s.injection_installation_id,
        offtakeInstallationId: s.offtake_installation_id,
      });
    };
    (portfolio?.services.bess ?? []).forEach((s) => addFlex(s, "BESS"));
    (portfolio?.services.eboiler ?? []).forEach((s) => addFlex(s, "e-boiler"));
    (portfolio?.services.pvCurtailment ?? []).forEach((s) => addFlex(s, "PV curtailment"));

    // Under-construction filter (mirrors the BE rule; 409 UNDER_CONSTRUCTION
    // is the backstop): drop EANs with no endable leg — single-direction
    // under construction, or a combined EAN with BOTH legs under
    // construction. A combined EAN with exactly ONE leg under construction
    // survives ONLY when directional EOC is allowed (the selector is then
    // forced to the connected leg); without it (Fluvius) 'Both' is the only
    // endable shape, so the whole EAN is blocked.
    // Keep a row if it is still submittable, OR it has a leg to surface in a
    // locked terminal state (confirmed 'approved', or already ending via a set
    // contract endDate) — the state stays visible. UC-only rows with no
    // outcome are still dropped.
    const commodities = [...map.values()].filter(
      (c) =>
        isCommoditySelectable(c) ||
        approvedDirectionsOf(c).length > 0 ||
        alreadyEndingDirectionsOf(c).length > 0
    );

    return [...commodities, ...flex];
  }, [portfolio]);

  const flexContracts = useMemo(
    () => eligibleContracts.filter((c): c is FlexEligibleContract => c.kind === "flex"),
    [eligibleContracts]
  );

  /** Keys whose request already went out — permanently locked. */
  const sentKeys = useMemo(() => {
    const keys = new Set<string>();
    outcomes.forEach((o, k) => {
      if (o.state === "sent") keys.add(k);
    });
    return keys;
  }, [outcomes]);

  const hasFailures = useMemo(
    () => [...outcomes.values()].some((o) => o.state !== "sent"),
    [outcomes]
  );

  /** Success-screen references, in submit order (Map preserves insertion order). */
  const requestIds = useMemo(() => {
    const ids: string[] = [];
    outcomes.forEach((o) => {
      if (o.state === "sent" && o.requestId) ids.push(o.requestId);
    });
    return ids;
  }, [outcomes]);

  const selectedCommodities = useMemo(
    () =>
      eligibleContracts.filter(
        (c): c is CommodityEligibleContract =>
          c.kind === "commodity" &&
          selectedCommodityKeys.has(c.key) &&
          !sentKeys.has(c.key) &&
          // An EAN that turned backend-pending (e.g. FE timeout + BE success,
          // surfaced by the status refetch) must never enter a new batch.
          !pendingByEan.has(c.key) &&
          // A confirmed 'approved'/terminal EAN (surfaced by /allservices) is
          // non-selectable — it can never re-enter a batch.
          isCommoditySelectable(c)
      ),
    [eligibleContracts, selectedCommodityKeys, sentKeys, pendingByEan]
  );
  const hasCommoditySelection = selectedCommodities.length > 0;

  /**
   * The direction the user picked for a combined+directional EAN (default
   * Both). A single-option row (one leg under construction) is FORCED to the
   * connected leg — the user's stored choice is ignored, and the POST always
   * carries `direction` explicitly (never an implicit whole-EAN end).
   */
  const directionChoiceFor = useCallback(
    (c: CommodityEligibleContract): EocDirection => {
      const options = directionOptionsFor(c);
      if (options.length === 0) return "both"; // no selector — plain row
      if (options.length === 1) return options[0]; // forced: connected leg only
      return directionChoices.get(c.key) ?? "both";
    },
    [directionChoices]
  );

  /**
   * Effective copy of a selected commodity: a combined EAN narrowed to one
   * direction gets `directions` reduced (drives linkage + badges) and
   * `eocDirection` set (drives the POST body). "Both" returns the row as-is.
   */
  const applyDirectionChoice = useCallback(
    (c: CommodityEligibleContract): CommodityEligibleContract => {
      const choice = directionChoiceFor(c);
      return choice === "both"
        ? c
        : { ...c, directions: [choice], eocDirection: choice };
    },
    [directionChoiceFor]
  );

  /** Selected commodities with the direction choice applied — the batch source. */
  const effectiveSelectedCommodities = useMemo(
    () => selectedCommodities.map(applyDirectionChoice),
    [selectedCommodities, applyDirectionChoice]
  );

  // Linked flex end TOGETHER with the selected commodity contracts (locked in
  // the UI) — union across ALL selected commodities, so injection + offtake
  // selected together links ALL flex on the site. Their actual ending is
  // handled downstream (site cascade on loss approval) — the FE submits only
  // the commodity requests.
  const linkedFlex = useMemo(
    () =>
      hasCommoditySelection
        ? flexContracts.filter((f) =>
            // EFFECTIVE directions: narrowing a combined EAN to Offtake drops
            // the injection-linked flex (PV) from the cascade, and vice versa.
            effectiveSelectedCommodities.some((commodity) =>
              isLinkedFlex(f, commodity)
            )
          )
        : [],
    [flexContracts, effectiveSelectedCommodities, hasCommoditySelection]
  );
  const linkedFlexKeys = useMemo(
    () => new Set(linkedFlex.map((f) => f.key)),
    [linkedFlex]
  );

  const selectedFlex = flexContracts.filter(
    (f) => selectedFlexKeys.has(f.key) && !sentKeys.has(f.key)
  );

  /**
   * Independently ticked flex — ended DIRECTLY via their own flex POST, even
   * alongside commodity selections (mixed batch). The linked-filter is
   * defensive: when a new commodity selection converts a ticked flex to
   * LINKED, the prune effect below removes it from selectedFlexKeys one
   * render later; this filter makes the submit set correct immediately.
   */
  const independentFlex = selectedFlex.filter((f) => !linkedFlexKeys.has(f.key));

  // Linked-conversion: a flex the user ticked independently that becomes
  // LINKED (via a commodity selection) must leave the direct-submit set — it
  // ends via the Atrias/mig6-ws site cascade instead, never a flex POST.
  // Deselecting that commodity releases it back to freely-tickable (unticked).
  useEffect(() => {
    if (linkedFlexKeys.size === 0) return;
    setSelectedFlexKeys((prev) => {
      const kept = [...prev].filter((k) => !linkedFlexKeys.has(k));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [linkedFlexKeys]);

  /**
   * What the FE actually POSTs: commodities first (effective copies — with
   * the direction choice applied), then independent flex.
   */
  const contractsToSubmit: EligibleContract[] = [
    ...effectiveSelectedCommodities,
    ...independentFlex,
  ];
  /** Everything that will end (confirm list): submit set + informative linked flex. */
  const contractsToEnd: EligibleContract[] = [...contractsToSubmit, ...linkedFlex];

  /**
   * Mode of the batch being confirmed/submitted — drives warning/success
   * copy. Once a batch is frozen the live selection can no longer be trusted
   * (the post-success portfolio refresh may have dropped rows out of the
   * eligible list).
   * - "commodity" — Atrias request-only (ending confirmed later)
   * - "flex"      — direct ending on the chosen date
   * - "mixed"     — both in one batch; copy must make the split explicit
   */
  const activeToSubmit = frozenBatch ? frozenBatch.toSubmit : contractsToSubmit;
  const batchMode: "commodity" | "flex" | "mixed" = (() => {
    const hasC = activeToSubmit.some((c) => c.kind === "commodity");
    const hasF = activeToSubmit.some((c) => c.kind === "flex");
    return hasC && hasF ? "mixed" : hasC ? "commodity" : "flex";
  })();
  /** Keys the batch actually POSTs — confirm rows NOT in here are linked flex. */
  const toSubmitKeys = new Set(activeToSubmit.map((c) => c.key));

  /**
   * Per-kind retry guard, recomputed every render (not memoized): a retry
   * resends the FROZEN dates, so it must be blocked when midnight has
   * advanced the Brussels minimum past a frozen date — but only for kinds that
   * still have something to resend, and never for an admin (exempt from the
   * minimum both here and BE-side). The rule is `computeEocDate()` — the
   * pre-FT-4390 "+28 / +1 calendar days" formula this used to validate against
   * was a fossil that could pass a date the backend rejects.
   */
  const retryBlocked = (() => {
    if (!frozenBatch || !hasFailures) return false;
    const pending = frozenBatch.toSubmit.filter(
      (c) => outcomes.get(c.key)?.state !== "sent"
    );
    return isEocRetryBlocked({
      isAdmin,
      hasPendingCommodity: pending.some((c) => c.kind === "commodity"),
      hasPendingFlex: pending.some((c) => c.kind === "flex"),
      frozenCommodityDate: frozenBatch.commodityEocDate,
      frozenFlexDate: frozenBatch.flexEocDate,
      minDate: eocDate,
    });
  })();

  /** Confirm-step rows: frozen at submit time so retries render a stable list. */
  const displayContracts = frozenBatch?.display ?? contractsToEnd;
  /**
   * The single end date shown on confirm. Frozen once a batch exists; before
   * that (confirm step reached but not yet submitted) it must reflect the
   * user's LIVE selection, not the default — commodityEocDate holds the picked
   * value (default or later). FT-4389: one date for every combo, commodity and
   * flex are kept equal in lockstep, so reading commodityEocDate is
   * authoritative.
   */
  const activeEocDate = frozenBatch?.commodityEocDate ?? commodityEocDate;

  /**
   * Confirmed-EOC-date renderer (FT-4389 T5): pure string-split DD/MM/YYYY,
   * NO Date parsing — avoids any timezone off-by-one on the YYYY-MM-DD input.
   */
  const formatEocDate = (iso: string | null): string => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    if (!y || !m || !d) return iso;
    return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
  };

  /**
   * Reject-reason CODE → localized copy (FT-4389 T6). The authoritative mig6
   * code list is pending; unmapped codes fall back to the raw code verbatim.
   */
  const rejectReasonCopy = (code: string | null): string => {
    const c = (code ?? "").trim();
    if (!c) return "";
    return t(`endContractEocRejectReason.${c}`, { defaultValue: c });
  };

  const toggleCommodity = (key: string) => {
    if (sentKeys.has(key)) return; // request already sent — permanently locked
    if (pendingByEan.has(key)) return; // backend-pending request — locked
    const isDeselect = selectedCommodityKeys.has(key);
    setSelectedCommodityKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    // Deselecting resets the row's direction choice back to Both.
    if (isDeselect) {
      setDirectionChoices((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const toggleFlex = (key: string) => {
    if (sentKeys.has(key)) return; // request already sent — permanently locked
    if (linkedFlexKeys.has(key)) return; // linked flex are locked ("Ends together")
    setSelectedFlexKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const directionLabel = (directions: Direction[]): string => {
    if (directions.length === 2) return t("endContractDirectionCombined");
    return directions[0] === "injection"
      ? t("endContractDirectionInjection")
      : t("endContractDirectionOfftake");
  };

  const flexTypeLabel = (assetType: FlexAssetType): string => {
    switch (assetType) {
      case "BESS":
        return t("endContractTypeBess");
      case "PV curtailment":
        return t("endContractTypePv");
      case "e-boiler":
        return t("endContractTypeEboiler");
    }
  };

  const contractLabel = (c: EligibleContract): string =>
    c.kind === "commodity" ? directionLabel(c.directions) : flexTypeLabel(c.assetType);

  const mapSubmitError = (err: unknown): string => {
    if (err instanceof EndOfContractError) {
      switch (err.code) {
        case "EFFECTIVE_DATE_TOO_SOON":
          return t("endContractErrorInvalidDate");
        case "NOT_BELGIUM":
          return t("endContractErrorNotBelgium");
        case "COMPANY_DATA_INCOMPLETE":
          return t("endContractErrorCompanyData");
        case "DGO_UNRESOLVED":
          return t("endContractErrorDgo");
        case "NO_SDP":
          return t("endContractErrorNoSdp");
        case "NO_ACTIVE_CONTRACT":
          return t("endContractErrorNoActiveContract");
        case "EOC_ALREADY_IN_FLIGHT":
          return t("endContractErrorAlreadyPending");
        case "LINKED_FLEX_INVALID":
          return t("endContractErrorLinkedFlexInvalid");
        case "DIRECTION_NOT_ALLOWED":
          return t("endContractErrorDirectionNotAllowed");
        case "UNDER_CONSTRUCTION":
          return t("endContractErrorUnderConstruction");
        case "CONTRACT_ALREADY_ENDING":
          return t("endContractErrorContractAlreadyEnding");
        case "ATRIAS_SEND_FAILED":
          return t("endContractErrorSendFailed");
        case "ATRIAS_UNAVAILABLE":
          return t("endContractErrorUnavailable");
        case "NO_FLEX_CONTRACT":
          return t("endContractErrorNoFlexContract");
        case "NOT_LATEST_CONTRACT":
          return t("endContractErrorNotLatest");
        case "NOT_ACTIVE":
          return t("endContractErrorNotActive");
        case "ALREADY_ENDING":
          return t("endContractErrorAlreadyEnding");
        default:
          break;
      }
      switch (err.status) {
        case 400:
          return t("endContractErrorInvalidDate");
        case 403:
          return t("endContractErrorNoAccess");
        case 404:
          return t("endContractErrorNoActiveContract");
        case 409:
          return t("endContractErrorAlreadyPending");
        case 502:
          return t("endContractErrorSendFailed");
        case 503:
          return t("endContractErrorUnavailable");
        default:
          return t("endContractErrorGeneric");
      }
    }
    return t("endContractErrorGeneric");
  };

  /**
   * One sequential submit pass over the frozen batch. Per-contract outcomes;
   * stop-on-first-error (the remainder is marked "notSent" — most failures
   * are systemic 502/503, hammering Atrias per-EAN makes it worse). Contracts
   * already "sent" are skipped, so a retry pass resends only failed/notSent.
   */
  const runSubmitPass = async (
    companyId: number,
    batch: SubmitBatch,
    isRetry: boolean
  ) => {
    if (!accessToken || batch.toSubmit.length === 0) return;
    setSubmitting(true);
    try {
      const next = new Map(outcomes);
      let interrupted = false;
      let newlySent = 0;
      let newlySentCommodity = 0;
      for (const contract of batch.toSubmit) {
        if (next.get(contract.key)?.state === "sent") continue; // never re-submit
        if (interrupted) {
          next.set(contract.key, { state: "notSent" });
          continue;
        }
        const body: EndOfContractRequestBody =
          contract.kind === "commodity"
            ? {
                contractType: "commodity",
                company_id: companyId,
                ean: contract.ean,
                effectiveDate: batch.commodityEocDate,
                // Directional EOC: only present when the user narrowed a
                // combined EAN to one direction (omitted = whole EAN).
                ...(contract.eocDirection ? { direction: contract.eocDirection } : {}),
                // Linked flex of THIS commodity (effective directions) —
                // persisted BE-side so mig6-ws cascade-ends them on loss
                // approval.
                ...buildLinkedFlexIds(contract, batch.linkedFlex),
              }
            : {
                contractType: "flex",
                company_id: companyId,
                contract_flex_id: contract.contractFlexId,
                effectiveDate: batch.flexEocDate,
              };
        try {
          const result = await sendEndOfContractRequest(body, accessToken);
          next.set(contract.key, {
            state: "sent",
            requestId: "requestId" in result.data ? result.data.requestId : null,
            alreadyProcessed: false,
          });
          newlySent++;
          if (contract.kind === "commodity") newlySentCommodity++;
        } catch (err) {
          // On a RETRY pass only: a 409 "already exists" means the previous
          // pass succeeded server-side but the response was lost — the exact
          // scenario this flow protects against. Treat it as sent (no id).
          // On a FIRST pass a 409 stays an error.
          if (
            isRetry &&
            err instanceof EndOfContractError &&
            err.status === 409 &&
            (err.code === "EOC_ALREADY_IN_FLIGHT" || err.code === "ALREADY_ENDING")
          ) {
            next.set(contract.key, {
              state: "sent",
              requestId: null,
              alreadyProcessed: true,
            });
            newlySent++;
            if (contract.kind === "commodity") newlySentCommodity++;
            continue;
          }
          console.error(err);
          next.set(contract.key, { state: "failed", message: mapSubmitError(err) });
          interrupted = true;
        }
      }
      setOutcomes(next);
      if (batch.toSubmit.every((c) => next.get(c.key)?.state === "sent")) {
        setStep("success");
      }
      // Refresh after every pass with at least one success so directly-ended
      // flex contracts drop out of the eligible list (end_date now set
      // backend-side) — not only on full success.
      if (newlySent > 0 && selectedCompanyNumber) {
        dispatch(
          fetchPortfolioServices({ companyNumber: selectedCompanyNumber, token: accessToken })
        );
      }
      // Pending indicator refresh — the just-sent commodity requests are now
      // backend-pending and their rows must lock accordingly.
      if (newlySentCommodity > 0) {
        void loadEocRequests();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFirstSubmit = () => {
    if (!selectedCompanyId) return;
    // Freeze the batch NOW: the post-success portfolio refresh shrinks the
    // live eligible list, and retries must still cover the full set — same
    // contracts, same linked flex ids, same per-kind dates.
    const batch: SubmitBatch = {
      toSubmit: contractsToSubmit,
      display: contractsToEnd,
      linkedFlex,
      commodityEocDate,
      flexEocDate,
    };
    setFrozenBatch(batch);
    void runSubmitPass(selectedCompanyId, batch, false);
  };

  const handleRetry = () => {
    if (!selectedCompanyId || !frozenBatch) return;
    void runSubmitPass(selectedCompanyId, frozenBatch, true);
  };

  const handleBackToForm = () => {
    // Sent outcomes are permanent (those rows stay locked in the form and can
    // never enter a fresh batch); failed/notSent are transient and cleared.
    setOutcomes((prev) => {
      const next = new Map<string, SubmitOutcome>();
      prev.forEach((o, k) => {
        if (o.state === "sent") next.set(k, o);
      });
      return next;
    });
    setFrozenBatch(null);
    setSelectedCommodityKeys(
      (prev) =>
        new Set(
          [...prev].filter((k) => !sentKeys.has(k) && !pendingByEan.has(k))
        )
    );
    setSelectedFlexKeys((prev) => new Set([...prev].filter((k) => !sentKeys.has(k))));
    setStep("form");
  };

  const outcomeChip = (outcome: SubmitOutcome) => {
    switch (outcome.state) {
      case "sent":
        return (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faCircleCheck} className="text-[10px]" />
            {t(
              outcome.alreadyProcessed
                ? "endContractOutcomeAlreadyProcessed"
                : "endContractOutcomeSent"
            )}
          </span>
        );
      case "failed":
        return (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faCircleXmark} className="text-[10px]" />
            {t("endContractOutcomeFailed")}
          </span>
        );
      case "notSent":
        return (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-primary-800 dark:text-primary-300">
            {t("endContractOutcomeNotSent")}
          </span>
        );
    }
  };

  /**
   * Confirmed-state badges for a commodity row. Per-leg, terminal-most first
   * (FT-4389): a slate "Already ending — ends <date>" per leg whose contract
   * endDate is set (SUPERSEDES accepted/rejected — those are skipped for that
   * leg, so no double-render), then a green "Accepted — ends <date>" per
   * approved leg, then a red "Rejected (<reason>)" per rejected leg. On
   * combined EANs each badge is prefixed with its leg so a mixed state
   * (offtake ending, injection open) is unambiguous.
   */
  const eocOutcomeBadges = (c: CommodityEligibleContract): React.ReactNode[] => {
    const isCombined = c.directions.length === 2;
    const legPrefix = (d: Direction) =>
      isCombined
        ? `${
            d === "offtake"
              ? t("endContractDirectionOfftake")
              : t("endContractDirectionInjection")
          }: `
        : "";
    const badges: React.ReactNode[] = [];
    const ending = alreadyEndingDirectionsOf(c);
    for (const d of ending) {
      const endDate = c.eocByDirection[d]?.contractEndDate ?? null;
      badges.push(
        <span
          key={`end-${d}`}
          className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200 inline-flex items-center gap-1"
        >
          <FontAwesomeIcon icon={faHourglassEnd} className="text-[10px]" />
          {legPrefix(d)}
          {isEndingDate(endDate)
            ? t("endContractEocAlreadyEndingOn", { date: formatEocDate(endDate) })
            : t("endContractEocAlreadyEnding")}
        </span>
      );
    }
    // Already-ending legs win: skip their accepted/rejected rendering.
    // badgeDirectionsOf, NOT approvedDirectionsOf: a switch-away leg ('39' +
    // reason 'COMPLETED') stays LOCKED but shows no EOC acceptance wording.
    for (const d of badgeDirectionsOf(c, "approved")) {
      if (ending.includes(d)) continue;
      const leg = c.eocByDirection[d];
      badges.push(
        <span
          key={`acc-${d}`}
          className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 inline-flex items-center gap-1"
        >
          <FontAwesomeIcon icon={faCircleCheck} className="text-[10px]" />
          {legPrefix(d)}
          {leg?.eocDate
            ? t("endContractEocApprovedEnds", { date: formatEocDate(leg.eocDate) })
            : t("endContractEocApprovedLabel")}
        </span>
      );
    }
    for (const d of badgeDirectionsOf(c, "rejected")) {
      if (ending.includes(d)) continue;
      const reason = rejectReasonCopy(c.eocByDirection[d]?.reason ?? null);
      badges.push(
        <span
          key={`rej-${d}`}
          className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 inline-flex items-center gap-1"
        >
          <FontAwesomeIcon icon={faCircleXmark} className="text-[10px]" />
          {legPrefix(d)}
          {reason
            ? t("endContractEocRejectedWithReason", { reason })
            : t("endContractEocRejectedLabel")}
        </span>
      );
    }
    return badges;
  };

  const renderContractIdentifier = (c: EligibleContract) =>
    c.kind === "commodity" ? (
      <span className="font-mono text-sm text-primary-600 dark:text-primary-300">
        {c.ean}
      </span>
    ) : (
      <span className="text-sm font-medium text-primary-600 dark:text-primary-300">
        {c.name}
      </span>
    );

  return (
    <div className="my-20 md:mx-14 text-primary-600 dark:text-primary-300">
      <button
        onClick={() => navigate("/management")}
        className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-600 dark:text-primary-300 dark:hover:text-white mb-6 transition-colors"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        {t("backToManagement")}
      </button>
      <h1 className="text-3xl font-semibold dark:text-white mb-4">
        {t("endContractTitle")}
      </h1>
      <p className="max-w-2xl text-primary-400 dark:text-primary-300 mb-10">
        {t("endContractIntro")}
      </p>

      {step === "form" && (
        <div className="max-w-2xl">
          <div className="p-6 rounded-lg border-[1px] border-base-200 dark:border-primary-800 bg-base-100 dark:bg-primary-900 mb-6">
            <h3 className="font-semibold text-primary-600 dark:text-white text-lg mb-4">
              {t("endContractSelectLabel")}
            </h3>

            {portfolioLoading ? (
              <div className="flex items-center gap-2 text-primary-400 py-4">
                <FontAwesomeIcon icon={faSpinner} spin />
                {t("loading")}
              </div>
            ) : portfolioError ? (
              // A failed /allservices is NOT an empty portfolio. Rendering
              // "no active contracts were found" for a fetch failure would
              // tell the customer something untrue about their own account.
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 flex gap-2">
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="mt-0.5 shrink-0"
                />
                <span>{t("endContractPortfolioError")}</span>
              </div>
            ) : eligibleContracts.length === 0 ? (
              <p className="text-sm text-primary-400 dark:text-primary-500 py-2">
                {t("endContractNoEligible")}
              </p>
            ) : (
              <ul className="space-y-2">
                {eligibleContracts.map((c) => {
                  const isCommodity = c.kind === "commodity";
                  const sentOutcome = outcomes.get(c.key);
                  const isSent = sentOutcome?.state === "sent";
                  // Only LINKED flex are locked while commodities are
                  // selected — non-linked flex stay freely tickable (mixed
                  // batches are allowed).
                  const isLinkedLocked =
                    !isSent && !isCommodity && linkedFlexKeys.has(c.key);
                  // Confirmed /allservices states (durable). Any of these on a
                  // leg supersedes the transient GET 'pending' badge for it.
                  // These are TERMINAL-STATE reads (the *DirectionsOf lock
                  // predicates), not badge reads: a switched-away leg is
                  // terminal, so it must still suppress a stale ledger
                  // 'pending' badge even though its own badge is hidden.
                  const approvedLegs =
                    c.kind === "commodity" && !isSent ? approvedDirectionsOf(c) : [];
                  const rejectedLegs =
                    c.kind === "commodity" && !isSent ? rejectedDirectionsOf(c) : [];
                  const alreadyEndingLegs =
                    c.kind === "commodity" && !isSent ? alreadyEndingDirectionsOf(c) : [];
                  // Fully-terminal commodity (nothing endable left) → locked.
                  const commodityTerminal =
                    c.kind === "commodity" && !isSent && !isCommoditySelectable(c);
                  // Backend-pending EOC request → the EAN is locked (keys of
                  // flex rows never collide with normalized EANs). The sent
                  // chip from THIS session takes precedence over the badge.
                  // TERMINAL WINS: a leg with a confirmed /allservices state
                  // (already ending / accepted / rejected) suppresses its
                  // transient GET 'pending' badge ('whole' pending is
                  // suppressed once either leg is terminal).
                  const rawPending = !isSent ? pendingByEan.get(c.key) : undefined;
                  const terminalDirs = new Set<Direction>([
                    ...approvedLegs,
                    ...rejectedLegs,
                    ...alreadyEndingLegs,
                  ]);
                  const showPending =
                    !!rawPending &&
                    (rawPending === "whole"
                      ? terminalDirs.size === 0
                      : !terminalDirs.has(rawPending));
                  const pendingDirection = showPending ? rawPending : undefined;
                  const isLocked =
                    isSent || isLinkedLocked || !!pendingDirection || commodityTerminal;
                  const isChecked = isSent
                    ? true
                    : isCommodity
                      ? selectedCommodityKeys.has(c.key)
                      : isLinkedLocked || selectedFlexKeys.has(c.key);
                  return (
                    <li key={c.key}>
                      <label
                        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                          isChecked
                            ? "border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-950"
                            : "border-gray-200 dark:border-primary-800"
                        } ${
                          isLocked
                            ? "cursor-not-allowed opacity-70"
                            : "cursor-pointer hover:border-primary-400 dark:hover:border-primary-500"
                        }`}
                      >
                        <input
                          type="checkbox"
                          name="end-contract-selection"
                          className="checkbox checkbox-sm mt-1"
                          checked={isChecked}
                          disabled={isLocked}
                          onChange={() =>
                            isCommodity ? toggleCommodity(c.key) : toggleFlex(c.key)
                          }
                        />
                        <span className="flex-1">
                          <span className="flex items-center justify-between gap-3 flex-wrap">
                            {renderContractIdentifier(c)}
                            <span className="flex items-center gap-2">
                              {sentOutcome?.state === "sent" && outcomeChip(sentOutcome)}
                              {pendingDirection && (
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                  {t(
                                    pendingDirection === "offtake"
                                      ? "endContractPendingBadgeOfftake"
                                      : pendingDirection === "injection"
                                        ? "endContractPendingBadgeInjection"
                                        : "endContractPendingBadge"
                                  )}
                                </span>
                              )}
                              {c.kind === "commodity" && !isSent && eocOutcomeBadges(c)}
                              {isLinkedLocked && (
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 inline-flex items-center gap-1">
                                  <FontAwesomeIcon icon={faLink} className="text-[10px]" />
                                  {t("endContractLinkedBadge")}
                                </span>
                              )}
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-800 text-primary-600 dark:text-primary-300">
                                {contractLabel(
                                  c.kind === "commodity" ? applyDirectionChoice(c) : c
                                )}
                              </span>
                            </span>
                          </span>
                          <span className="block text-sm text-primary-400 dark:text-primary-500 mt-1">
                            {c.kind === "commodity" && c.siteName
                              ? `${c.siteName} — `
                              : ""}
                            {c.address}
                          </span>
                          {c.kind === "commodity" &&
                            isChecked &&
                            !isSent &&
                            directionOptionsFor(c).length > 0 && (
                              <span className="block mt-2">
                                <span className="block text-xs text-primary-400 dark:text-primary-500 mb-1">
                                  {t("endContractDirectionChoiceLabel")}
                                </span>
                                <span className="inline-flex rounded-lg border border-gray-200 dark:border-primary-700 overflow-hidden">
                                  {directionOptionsFor(c).map(
                                    (option) => {
                                      const active = directionChoiceFor(c) === option;
                                      return (
                                        <button
                                          key={option}
                                          type="button"
                                          onClick={(e) => {
                                            // Inside a <label>: block the
                                            // checkbox toggle.
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setDirectionChoices((prev) => {
                                              const next = new Map(prev);
                                              next.set(c.key, option);
                                              return next;
                                            });
                                          }}
                                          className={`px-3 py-1 text-xs font-semibold transition-colors ${
                                            active
                                              ? "bg-primary-600 text-white"
                                              : "bg-white dark:bg-primary-800 text-primary-600 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-700"
                                          }`}
                                        >
                                          {option === "offtake"
                                            ? t("endContractDirectionOfftake")
                                            : option === "injection"
                                              ? t("endContractDirectionInjection")
                                              : t("endContractDirectionBoth")}
                                        </button>
                                      );
                                    }
                                  )}
                                </span>
                              </span>
                            )}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}

            {hasCommoditySelection && linkedFlex.length > 0 && (
              <p className="text-sm text-primary-400 dark:text-primary-300 mt-4">
                {t("endContractLinkedNote")}
              </p>
            )}
          </div>

          {/* FT-4389 R1: the end-date field appears only once at least one
              contract is selected. ONE date for every combo (commodity / flex
              / mixed), and onChange keeps the commodity and flex dates in
              lockstep in BOTH role branches.

              Role split (portals-backend #476 relaxes the same two rules
              server-side for an admin, resolved from the JWT):
              - non-admin → MonthPicker, MONTH granularity, minDate = eocDate.
                Unchanged behaviour: pick that month or any later one.
              - admin     → DatePicker, DAY granularity, NO minDate at all.
                Same computeEocDate() default, then freely editable — earlier,
                later, mid-month. No warning and no soft gate is shown: what
                Atrias does with a short-notice or mid-month date is
                deliberately the admin's call. */}
          {contractsToEnd.length > 0 && (
            <div className="p-6 rounded-lg border-[1px] border-base-200 dark:border-primary-800 bg-base-100 dark:bg-primary-900 mb-6">
              <h3 className="font-semibold text-primary-600 dark:text-white text-lg mb-2">
                {t("endContractDateLabel")}
              </h3>
              {isAdmin ? (
                <DatePicker
                  className="max-w-xs w-full"
                  value={commodityEocDate}
                  onChange={handleEocDateChange}
                />
              ) : (
                <MonthPicker
                  className="max-w-xs w-full"
                  value={commodityEocDate}
                  minDate={eocDate}
                  onChange={handleEocDateChange}
                />
              )}
              <p className="text-sm text-primary-400 dark:text-primary-500 mt-2">
                {isAdmin
                  ? t("endContractDateHintAdmin", { date: formatEocDate(eocDate) })
                  : t("endContractDateHint", { date: formatEocDate(eocDate) })}
              </p>
            </div>
          )}

          <button
            className="px-5 py-2 bg-primary-600 text-white rounded-3xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            // The minimum is the SECOND enforcement site (the picker is the
            // first, retryBlocked the third) and must be role-aware too:
            // without this an admin could pick an earlier date and then find
            // Continue silently dead.
            disabled={
              contractsToEnd.length === 0 ||
              (!isAdmin && commodityEocDate < eocDate)
            }
            onClick={() => setStep("confirm")}
          >
            {t("endContractContinue")}
          </button>
        </div>
      )}

      {step === "confirm" && displayContracts.length > 0 && (
        <div className="max-w-2xl">
          <div className="p-6 rounded-lg border-[1px] border-base-200 dark:border-primary-800 bg-base-100 dark:bg-primary-900 mb-6">
            <h3 className="font-semibold text-primary-600 dark:text-white text-lg mb-4">
              {t(hasFailures ? "endContractPartialTitle" : "endContractConfirmTitle")}
            </h3>

            <span className="text-sm text-primary-400 dark:text-primary-500 block mb-2">
              {t("endContractSelectedServices")}
            </span>
            <ul className="space-y-2 mb-4">
              {displayContracts.map((c) => {
                // Linked flex never get an outcome — they are informative
                // only (their ending is the backend's site cascade).
                const outcome = outcomes.get(c.key);
                return (
                  <li
                    key={c.key}
                    className="flex items-start justify-between gap-3 p-3 rounded-lg border border-gray-200 dark:border-primary-800"
                  >
                    <span>
                      {renderContractIdentifier(c)}
                      <span className="block text-sm text-primary-400 dark:text-primary-500 mt-1">
                        {c.address}
                      </span>
                      {outcome?.state === "sent" && outcome.requestId && (
                        <span className="block text-xs font-mono text-primary-400 dark:text-primary-500 mt-1">
                          {t("endContractReference")}: {outcome.requestId}
                        </span>
                      )}
                      {outcome?.state === "failed" && (
                        <span className="block text-sm text-red-600 dark:text-red-400 mt-1">
                          {outcome.message}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      {outcome && outcomeChip(outcome)}
                      {!outcome && c.kind === "flex" && !toSubmitKeys.has(c.key) && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 inline-flex items-center gap-1">
                          <FontAwesomeIcon icon={faLink} className="text-[10px]" />
                          {t("endContractLinkedBadge")}
                        </span>
                      )}
                      {!outcome && c.kind === "flex" && toSubmitKeys.has(c.key) && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                          {t("endContractDirectBadge")}
                        </span>
                      )}
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-800 text-primary-600 dark:text-primary-300">
                        {contractLabel(c)}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>

            {/* FT-4389: a single deterministic end date for every combo —
                one date line regardless of commodity / flex / mixed. */}
            <div className="text-sm mb-4">
              <span className="text-primary-400 dark:text-primary-500 block">
                {t("endContractConfirmDate")}
              </span>
              <span className="font-semibold text-primary-600 dark:text-white">
                {formatEocDate(activeEocDate)}
              </span>
            </div>

            {displayContracts.some(
              (c) => c.kind === "flex" && !toSubmitKeys.has(c.key)
            ) && (
              <div className="p-3 rounded-lg bg-primary-50 border border-primary-200 text-primary-700 text-sm dark:bg-primary-950 dark:border-primary-800 dark:text-primary-300 mb-3">
                {t("endContractLinkedNote")}
              </div>
            )}

            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300 flex gap-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" />
              <span>
                {t(
                  batchMode === "mixed"
                    ? "endContractConfirmWarningMixed"
                    : batchMode === "commodity"
                      ? "endContractConfirmWarning"
                      : "endContractConfirmWarningFlex"
                )}
              </span>
            </div>

            {hasFailures && (
              <div className="mt-4 p-3 rounded-lg bg-primary-50 border border-primary-200 text-primary-700 text-sm dark:bg-primary-950 dark:border-primary-800 dark:text-primary-300">
                {t("endContractPartialNote")}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              className="px-5 py-2 bg-primary-600 text-white rounded-3xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 transition-colors"
              disabled={submitting || !selectedCompanyId || retryBlocked}
              onClick={hasFailures ? handleRetry : handleFirstSubmit}
            >
              {submitting && <FontAwesomeIcon icon={faSpinner} spin />}
              {submitting
                ? t("endContractSubmitting")
                : t(hasFailures ? "endContractRetryFailed" : "endContractSubmit")}
            </button>
            <button
              className="px-4 py-2 text-sm font-semibold text-primary-500 hover:text-primary-700 dark:text-primary-300 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting}
              onClick={handleBackToForm}
            >
              {t("endContractBack")}
            </button>
          </div>
        </div>
      )}

      {step === "success" && (
        <div className="max-w-2xl">
          <div className="p-6 rounded-lg border-[1px] border-base-200 dark:border-primary-800 bg-base-100 dark:bg-primary-900">
            <h3 className="font-semibold text-primary-600 dark:text-white text-lg mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faCircleCheck} className="text-green-600" />
              {t("endContractSuccessTitle")}
            </h3>
            <p className="text-sm text-primary-400 dark:text-primary-300 mb-4">
              {t(
                batchMode === "mixed"
                  ? "endContractSuccessTextMixed"
                  : batchMode === "commodity"
                    ? "endContractSuccessText"
                    : "endContractSuccessTextFlex"
              )}
            </p>
            {requestIds.length > 0 && (
              <p className="text-sm text-primary-400 dark:text-primary-500 mb-4">
                {t("endContractReference")}:{" "}
                <span className="font-mono text-primary-600 dark:text-primary-300">
                  {requestIds.join(", ")}
                </span>
              </p>
            )}
            <button
              className="px-5 py-2 bg-primary-600 text-white rounded-3xl text-sm font-semibold hover:bg-primary-700 transition-colors"
              onClick={() => navigate("/management")}
            >
              {t("backToManagement")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EndContractManager;
